// Face ID / Touch ID unlock via WebAuthn PRF.
//
// Flow:
//   Enable: user is already unlocked with master password →
//     navigator.credentials.create({ extensions: { prf: { eval: { first: salt } } } })
//     Apple's secure enclave returns a PRF output (deterministic for this salt+credential).
//     We AES-GCM encrypt the in-memory vault_key with that PRF output,
//     persist {credentialId, salt, nonce, ciphertext} in localStorage.
//
//   Unlock: navigator.credentials.get with allowCredentials=[credentialId] + same PRF salt
//     → Face ID prompt → PRF output (same as enable time)
//     → AES-GCM decrypt → vault_key back in memory.
//
// Requires Safari 17.4+ on iOS / macOS, Chrome 132+, Firefox not yet.
// PRF support is feature-detected per-credential; we throw a clear error if absent.

const RP_NAME = "Sejf";
const USER_LABEL = "Sejf vault";

function rpId(): string {
  // WebAuthn rp.id must equal location.hostname or a parent. Just use the hostname.
  return window.location.hostname;
}

// ---------- base64 helpers (Uint8Array <-> string) ----------

export function bytesToB64(b: Uint8Array): string {
  let s = "";
  for (const byte of b) s += String.fromCharCode(byte);
  return btoa(s);
}

export function b64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------- AES-GCM via Web Crypto ----------

async function aesGcmEncrypt(
  plaintext: Uint8Array,
  keyBytes: Uint8Array,
): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes.slice(0, 32) as BufferSource,
    "AES-GCM",
    false,
    ["encrypt"],
  );
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    plaintext as BufferSource,
  );
  return { ciphertext: new Uint8Array(ct), nonce };
}

async function aesGcmDecrypt(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  keyBytes: Uint8Array,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes.slice(0, 32) as BufferSource,
    "AES-GCM",
    false,
    ["decrypt"],
  );
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    ciphertext as BufferSource,
  );
  return new Uint8Array(plaintext);
}

// ---------- WebAuthn PRF ----------

// TS lib.dom doesn't have PRF extension types in older versions. Cast to a loose shape.
type PrfExtensionResults = {
  prf?: { results?: { first?: ArrayBuffer; second?: ArrayBuffer } };
};

function isFaceIdSupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";
}

async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!isFaceIdSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export async function faceIdAvailable(): Promise<boolean> {
  return await platformAuthenticatorAvailable();
}

async function registerCredential(
  prfSalt: Uint8Array,
): Promise<{ credentialId: Uint8Array; prfOutput: Uint8Array | null }> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  // Deterministic-ish user id; the relying party (us) is single-user so identity
  // doesn't really matter to the authenticator beyond uniqueness per RP.
  const userId = new TextEncoder().encode("sejf-user-1");

  const cred = (await navigator.credentials.create({
    publicKey: {
      rp: { name: RP_NAME, id: rpId() },
      user: {
        id: userId as BufferSource,
        name: USER_LABEL,
        displayName: USER_LABEL,
      },
      challenge: challenge as BufferSource,
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform", // Face ID / Touch ID
        userVerification: "required",
        residentKey: "discouraged",
      },
      timeout: 60_000,
      attestation: "none",
      extensions: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        prf: { eval: { first: prfSalt as BufferSource } },
      } as unknown as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) throw new Error("Face ID: rejestracja anulowana");

  const ext = (cred.getClientExtensionResults() as unknown) as PrfExtensionResults;
  const first = ext?.prf?.results?.first;
  return {
    credentialId: new Uint8Array(cred.rawId),
    prfOutput: first ? new Uint8Array(first) : null,
  };
}

const PRF_HELP =
  "Wybrany dostawca kluczy nie wspiera szyfrowania Face ID. " +
  "Wejdź w Ustawienia iOS → Hasła → Opcje haseł, " +
  "włącz iCloud Passwords i (jeśli masz) wyłącz Microsoft Authenticator " +
  "jako dostawcę kluczy dostępu, potem spróbuj ponownie.";

async function evaluatePrf(
  credentialId: Uint8Array,
  prfSalt: Uint8Array,
): Promise<Uint8Array> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: challenge as BufferSource,
      allowCredentials: [
        { type: "public-key", id: credentialId as BufferSource },
      ],
      userVerification: "required",
      timeout: 60_000,
      rpId: rpId(),
      extensions: {
        prf: { eval: { first: prfSalt as BufferSource } },
      } as unknown as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new Error("Face ID: anulowane");
  const ext = (assertion.getClientExtensionResults() as unknown) as PrfExtensionResults;
  const first = ext?.prf?.results?.first;
  if (!first) throw new Error(PRF_HELP);
  return new Uint8Array(first);
}

// ---------- Public API ----------

export interface FaceIdBundle {
  credentialIdB64: string;
  saltB64: string;
  nonceB64: string;
  ciphertextB64: string;
}

/** Register a Face ID credential and wrap `vaultKey` with the resulting PRF secret.
 * Returns a bundle the caller should persist. */
export async function enrollFaceId(vaultKey: Uint8Array): Promise<FaceIdBundle> {
  if (!(await faceIdAvailable())) {
    throw new Error("Twoje urządzenie nie wspiera Face ID dla aplikacji webowych");
  }
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const { credentialId, prfOutput: initialPrf } = await registerCredential(salt);

  // Apple's Safari typically returns PRF only on a follow-up get(); fall back to that.
  // If even the second call fails, the chosen passkey provider doesn't support PRF
  // (e.g. Microsoft Authenticator). We don't want to leave a useless credential
  // dangling in the provider's storage, but we also can't delete it programmatically
  // — at least show a clear error so the user knows what to do.
  let prfOutput: Uint8Array;
  if (initialPrf) {
    prfOutput = initialPrf;
  } else {
    try {
      prfOutput = await evaluatePrf(credentialId, salt);
    } catch (e) {
      // Re-throw with the helpful Polish message regardless of underlying cause.
      throw new Error(PRF_HELP);
    }
  }

  const { ciphertext, nonce } = await aesGcmEncrypt(vaultKey, prfOutput);
  return {
    credentialIdB64: bytesToB64(credentialId),
    saltB64: bytesToB64(salt),
    nonceB64: bytesToB64(nonce),
    ciphertextB64: bytesToB64(ciphertext),
  };
}

/** Prompt Face ID and unwrap the stored vault key. */
export async function unwrapVaultKey(bundle: FaceIdBundle): Promise<Uint8Array> {
  const credentialId = b64ToBytes(bundle.credentialIdB64);
  const salt = b64ToBytes(bundle.saltB64);
  const prfOutput = await evaluatePrf(credentialId, salt);
  return await aesGcmDecrypt(
    b64ToBytes(bundle.ciphertextB64),
    b64ToBytes(bundle.nonceB64),
    prfOutput,
  );
}
